import { CascaderField, type CascaderOption } from '@/components';
import { useProjectFormContext } from '@/features/project/_hooks/form-context';
import { useSnapshots } from '@/features/tenant-manager/snapshots/_hooks/useSnapshot';
import dayjs from 'dayjs';
import { groupBy } from 'lodash-es';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const SnapshotField: FC = () => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    watch,
  } = useProjectFormContext();

  const [fiscalYear] = watch(['data.fiscalYear']);

  const snapshots = useSnapshots({
    params: { referenceFiscalYear: fiscalYear?.year().toString() },
    options: { onlySuccess: true },
    enabled: !!fiscalYear,
  });

  const snapshotOptions = useMemo<CascaderOption[]>(() => {
    if (!snapshots.data) {
      return [];
    }
    const snapshotByFiscalYear = groupBy(
      snapshots.data.map((v) => {
        return { ...v, fiscalYear: dayjs(v.referenceDate).year() };
      }),
      'fiscalYear',
    );

    return Object.entries(snapshotByFiscalYear).map(([fiscalYear, data]) => {
      return {
        label: fiscalYear,
        value: fiscalYear,
        children: data.map((v) => ({ label: v.name, value: v.id })),
      };
    });
  }, [snapshots.data]);

  return (
    <CascaderField
      key="data.snapshotId"
      name="data.fiscalYearOfSnapshot"
      label={t('snapshots-name')}
      hint={t('hint-snapshot-reference-date')}
      control={control}
      errors={errors}
      disabled={!fiscalYear}
      loading={snapshots.isPending}
      options={snapshotOptions}
      displayRender={(labels) => {
        return labels[labels.length - 1];
      }}
    />
  );
};
